/**
 * RX Product Page Web Components
 * Prescription eyewear product page using native Web Components
 * BEM Architecture - No Tailwind dependencies
 */

// ============================================================================
// STATE MANAGER
// ============================================================================

class RxState extends EventTarget {
  #state = {
    // Frame product
    product: null,
    selectedVariant: null,
    productOptions: null,

    // RX Lens product
    rxLensProduct: null,
    rxLensSelectedVariant: null,
    rxLensProductOptions: null,
    rxLensProducts: [],

    // Vision Types (Single Vision / Progressive)
    rxVisionTypes: [],      // All vision types for current lens category
    rxVisionType: null,     // Currently selected vision type { label, products }
    rxStyle: null,          // 'SV', 'Readers', or 'Progressive'
    splitSvReaders: false,  // Whether to split Single Vision into SV + Readers tabs

    // Tags and filters
    activeTags: {
      category: null,
      frame: null,
      eyewear: null,
    },
    frameTagForGettingCorrectRxLensProduct: 'DEFAULT',

    // Shop products (all frames)
    shopProducts: {},
    framesByGroup: [],

    // UI State
    isOpenPrescriptionPopup: false,
    isOpenFramePopup: false,
    loading: false,

    // Prescription form
    prescriptionStep: 'INITIAL', // INITIAL, MANUAL (removed UPLOAD step)
    isAnalyzing: false, // Shows loader while ScriptAI analyzes prescription
    prescriptionFile: null,
    prescriptionFilePath: null,
    prescriptionUniqueId: null,
    prescriptionRxUID: null,
    prescriptionData: null, // Analyzed prescription data from ScriptAI
    isUploading: false,
    manualFormField: {
      SPH: { R: 'Select', L: 'Select' },
      CYL: { R: 'Select', L: 'Select' },
      AXIS: { R: 'Select', L: 'Select' },
      PUPIL: { R: 'Select', L: 'Select' },
      PUPIL_RL: { RL: 'Select' },
      ADD: { R: 'Select', L: 'Select' },
      PRISM: {
        R: { vertical: { value: 'Select', direction: '' }, horizontal: { value: 'Select', direction: '' } },
        L: { vertical: { value: 'Select', direction: '' }, horizontal: { value: 'Select', direction: '' } }
      }
    },
    isOpenPD: false,
    isOpenPrism: false,

    // Gallery images
    images: [],
  };

  constructor() {
    super();
  }

  get(key) {
    if (key.includes('.')) {
      return key.split('.').reduce((obj, k) => obj?.[k], this.#state);
    }
    return this.#state[key];
  }

  set(key, value, silent = false) {
    const oldValue = this.get(key);

    if (key.includes('.')) {
      const keys = key.split('.');
      const lastKey = keys.pop();
      const target = keys.reduce((obj, k) => obj[k], this.#state);
      target[lastKey] = value;
    } else {
      this.#state[key] = value;
    }

    if (!silent && oldValue !== value) {
      this.dispatchEvent(new CustomEvent('state:change', {
        detail: { key, value, oldValue }
      }));

      this.dispatchEvent(new CustomEvent(`state:${key}`, {
        detail: { value, oldValue }
      }));
    }
  }

  getState() {
    return { ...this.#state };
  }

  setState(updates, silent = false) {
    Object.entries(updates).forEach(([key, value]) => {
      this.set(key, value, silent);
    });
  }
}

// Global state instance
window.rxState = new RxState();

// ============================================================================
// UTILITIES
// ============================================================================

// RX Proxy config - loaded from theme settings via Liquid (window.RX_PROXY_CONFIG)
// Requests go through Shopify App Proxy (/apps/proxy/...) — Shopify signs them server-side.
const RX_PROXY_CONFIG = window.RX_PROXY_CONFIG || {
  SUBDOMAIN: 'foc',
  PROVIDER_NUMBER: '592177MH'
};

const RxUtils = {
  formatMoney(cents, showCurrency = true) {
    const amount = (cents / 100).toFixed(2);
    return showCurrency ? `$${amount}` : amount;
  },

  titleCase(str) {
    return str.toLowerCase().replace(/\b\w/g, s => s.toUpperCase());
  },

  handleize(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  },

  debounce(fn, delay) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  },

  async fetchProducts(url) {
    try {
      const response = await fetch(url);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const data = doc.getElementById('all-products')?.textContent;
      return data ? JSON.parse(data) : {};
    } catch (e) {
      console.error('Failed to fetch products:', e);
      return {};
    }
  },

  // Analyze prescription image via RX Proxy
  async analyzePrescription(file) {
    const formData = new FormData();
    formData.append('action', 'analyze-prescription');
    formData.append('prescription', file);

    const response = await fetch('/apps/proxy/analyze-prescription', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Analysis failed (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    const responseData = result.data || result;

    return {
      success: result.success || responseData.success || true,
      prescriptionData: responseData.prescriptionData,
      uniqueId: responseData.uniqueId || responseData.unique_id,
      analysis: responseData.analysis,
      rxUID: responseData.rxUID
    };
  },

  // Save prescription to RX Proxy
  async savePrescription({ orderId, lineItemId, prescriptionData }) {
    const payload = {
      action: 'save-prescription',
      order_id: orderId,
      line_item_id: lineItemId,
      ...prescriptionData
    };

    const response = await fetch('/apps/proxy/save-prescription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Save failed (${response.status})`);
    }

    return response.json();
  },

  // Convert single-letter prism directions to full words for API
  convertPrismDirection(dir) {
    const map = { 'O': 'Out', 'I': 'In', 'U': 'Up', 'D': 'Down' };
    return map[dir] || dir;
  },

  // Build prescription payload for save-prescription API call
  buildPrescriptionPayload(manualFormField, isOpenPD, isOpenPrism) {
    const f = manualFormField;
    const s = (v) => v === 'Select' ? '0.00' : v;

    const prescriptionData = {
      sphRight: s(f.SPH.R),
      sphLeft: s(f.SPH.L),
      cylRight: s(f.CYL.R),
      cylLeft: s(f.CYL.L),
      axisRight: s(f.AXIS.R) === '0.00' ? '000' : s(f.AXIS.R),
      axisLeft: s(f.AXIS.L) === '0.00' ? '000' : s(f.AXIS.L),
      addRight: f.ADD.R === 'Select' || f.ADD.R === 'N/A' ? '0.00' : f.ADD.R,
      addLeft: f.ADD.L === 'Select' || f.ADD.L === 'N/A' ? '0.00' : f.ADD.L,
      isMonocularPd: isOpenPD,
    };

    if (isOpenPD) {
      prescriptionData.pdRight = (f.PUPIL.R !== 'Select' ? f.PUPIL.R : '0') + 'mm';
      prescriptionData.pdLeft = (f.PUPIL.L !== 'Select' ? f.PUPIL.L : '0') + 'mm';
    } else {
      prescriptionData.pdValue = (f.PUPIL_RL.RL !== 'Select' ? f.PUPIL_RL.RL : '0') + 'mm';
    }

    if (isOpenPrism) {
      const rh = f.PRISM.R.horizontal;
      const rv = f.PRISM.R.vertical;
      const lh = f.PRISM.L.horizontal;
      const lv = f.PRISM.L.vertical;
      const hasPrism = [rh.value, rv.value, lh.value, lv.value].some(v => v !== 'Select' && v !== '0.00');

      if (hasPrism) {
        prescriptionData.prismFormat = 'directional';
        prescriptionData.prismRight = {
          horizontal: { value: rh.value === 'Select' ? '0.00' : rh.value, base: this.convertPrismDirection(rh.direction) || 'Out' },
          vertical: { value: rv.value === 'Select' ? '0.00' : rv.value, base: this.convertPrismDirection(rv.direction) || 'Up' }
        };
        prescriptionData.prismLeft = {
          horizontal: { value: lh.value === 'Select' ? '0.00' : lh.value, base: this.convertPrismDirection(lh.direction) || 'Out' },
          vertical: { value: lv.value === 'Select' ? '0.00' : lv.value, base: this.convertPrismDirection(lv.direction) || 'Up' }
        };
      }
    }

    return { data: { prescriptionData } };
  },

  // Calculate Health Fund Item Numbers based on lens type and eyewear
  getHealthFundItemNumbers(rxLensProduct, rxLensSelectedVariant) {
    if (!rxLensProduct || !rxLensSelectedVariant) return '';

    const eyewear = rxLensProduct.tags?.find(tag => tag.includes('eyewear_'));
    const lensVariantOptions = rxLensSelectedVariant.options?.join().toLowerCase() || '';
    const values = [110]; // Base code

    // Lens type codes
    if (lensVariantOptions.includes('reading')) {
      values.push(212, 612);
    } else if (lensVariantOptions.includes('distance')) {
      values.push(512, 612);
    } else {
      values.push(212, 612);
    }

    // Eyewear type codes
    switch (eyewear) {
      case 'eyewear_Sunglasses':
        values.push(632);
        break;
      case 'eyewear_Photochromic':
        values.push(644);
        break;
    }

    return values.join(', ');
  },

  // Build line item properties for cart
  buildLineItemProperties({
    product,
    selectedVariant,
    rxLensProduct,
    rxLensSelectedVariant,
    prescriptionType,
    prescriptionUniqueId,
    prescriptionRxUID,
    pdValue,
    pdLeft,
    pdRight,
    isOpenPD,
    rxStyle
  }) {
    const activeTags = rxLensProduct?.tags || [];
    const eyewearTag = activeTags.find(tag => tag.includes('eyewear_'));
    const lensStyle = eyewearTag?.replace('eyewear_', '') || 'Clear';

    // Health fund
    const healthFundItemNumbers = this.getHealthFundItemNumbers(rxLensProduct, rxLensSelectedVariant);

    // Base properties (без Frame Description)
    const properties = {
      _prescription_type: prescriptionType || 'pending',
      'Provider number': RX_PROXY_CONFIG.PROVIDER_NUMBER,
      'Health Fund Item Numbers': healthFundItemNumbers,
      _frame_variant_id: selectedVariant?.id,
      rxOrder: 'true',
      'Frame SKU': selectedVariant?.sku || '',
      'Lens SKU': rxLensSelectedVariant?.sku || '',
      'Lens Style': lensStyle,
      'RX Style': rxStyle || 'SV'
    };

    // Prescription IDs
    if (prescriptionUniqueId) {
      properties['uniqueId'] = prescriptionUniqueId;
    }
    if (prescriptionRxUID) {
      properties['Prescription RX UID'] = prescriptionRxUID;
    }

    // PD values
    if (isOpenPD && pdLeft && pdRight) {
      properties['Pupillary Distance Left'] = `${pdLeft}mm`;
      properties['Pupillary Distance Right'] = `${pdRight}mm`;
    } else if (pdValue) {
      properties['Pupillary Distance'] = `${pdValue}mm`;
    }

    return properties;
  }
};

window.RxUtils = RxUtils;

// ============================================================================
// BASE COMPONENT CLASS
// ============================================================================

class RxBaseComponent extends HTMLElement {
  #abortController = null;

  constructor() {
    super();
  }

  connectedCallback() {
    this.#abortController = new AbortController();
    this.setupListeners();
    this.init();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.cleanup();
  }

  get signal() {
    return this.#abortController?.signal;
  }

  get state() {
    return window.rxState;
  }

  // Override in subclasses
  init() {}
  setupListeners() {}
  cleanup() {}

  // Helper to add event listeners with automatic cleanup
  listen(target, event, handler, options = {}) {
    target.addEventListener(event, handler, {
      signal: this.signal,
      ...options
    });
  }

  // Subscribe to state changes
  onStateChange(key, handler) {
    this.listen(this.state, `state:${key}`, (e) => handler(e.detail));
  }

  // Emit custom events
  emit(name, detail = {}) {
    this.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      composed: true,
      detail
    }));
  }
}

// ============================================================================
// RX-PRODUCT-PAGE - Root Container
// ============================================================================

class RxProductPage extends RxBaseComponent {
  init() {
    this.loadInitialData();
    this.loadShopProducts();
  }

  setupListeners() {
    // Listen for product changes
    this.listen(this, 'rx:frame-change', (e) => this.handleFrameChange(e.detail));
    this.listen(this, 'rx:lens-type-change', (e) => this.handleLensTypeChange(e.detail));
    this.listen(this, 'rx:vision-type-change', (e) => this.handleVisionTypeChange(e.detail));
    this.listen(this, 'rx:lens-option-change', (e) => this.handleLensOptionChange(e.detail));

    // Listen for UI state changes
    this.listen(this, 'rx:prescription-open', () => this.openPrescription());
    this.listen(this, 'rx:prescription-close', () => this.closePrescription());
    this.listen(this, 'rx:frame-popup-toggle', () => this.toggleFramePopup());
  }

  deriveRxStyle(visionType) {
    const label = (visionType?.label || '').toLowerCase();
    if (label.includes('progressive') || label.includes('multifocal') || label.includes('bifocal')) return 'Progressive';
    if (label.includes('reader')) return 'Readers';
    return 'SV';
  }

  loadInitialData() {
    const dataEl = this.querySelector('[data-rx-product-data]');
    if (!dataEl) {
      console.warn('RX Product: No data element found [data-rx-product-data]');
      return;
    }

    try {
      const data = JSON.parse(dataEl.textContent);

      const { product, productOptions, selectedVariant, rxLensProducts, frameTagForGettingCorrectRxLensProduct, splitSvReaders } = data;

      // Filter out rxLensProducts that don't have valid visionTypes with products
      const validRxLensProducts = (rxLensProducts || []).filter(p => {
        // Check if has visionTypes array with at least one valid vision type
        if (p.visionTypes && Array.isArray(p.visionTypes)) {
          return p.visionTypes.some(vt =>
            vt.products && Object.keys(vt.products).length > 0 &&
            vt.products[frameTagForGettingCorrectRxLensProduct]
          );
        }
        return false;
      });

      // Get first lens category's vision types
      const firstLensCategory = validRxLensProducts[0];
      const visionTypes = firstLensCategory?.visionTypes || [];
      const firstVisionType = visionTypes[0];
      const firstRxLensProduct = firstVisionType?.products?.[frameTagForGettingCorrectRxLensProduct];

      // Set initial state
      this.state.setState({
        product,
        productOptions,
        selectedVariant: selectedVariant || product.variants[0],
        rxLensProducts: validRxLensProducts,
        frameTagForGettingCorrectRxLensProduct,
        splitSvReaders: !!splitSvReaders,
        rxVisionTypes: visionTypes,
        rxVisionType: firstVisionType,
        rxStyle: this.deriveRxStyle(firstVisionType),
        rxLensProduct: firstRxLensProduct,
        rxLensSelectedVariant: firstRxLensProduct?.selectedVariant,
        rxLensProductOptions: firstRxLensProduct?.productOptions
      });

      // Set active tags
      this.setActiveTags(product.tags);
      if (firstRxLensProduct?.tags) {
        this.setActiveTags(firstRxLensProduct.tags);
      }

      // Set initial images
      this.updateGalleryImages();

      // Update URL
      this.updateUrl();

      // Mark as initialized
      this.classList.add('initialized');

    } catch (e) {
      console.error('RX Product: Failed to load initial data:', e);
    }
  }

  async loadShopProducts() {
    const shopProducts = await RxUtils.fetchProducts('/pages/return-policy?view=prescription-products-json');
    this.state.set('shopProducts', shopProducts);

    // Build frames by group
    const framesByGroupMap = new Map();
    Object.values(shopProducts).forEach(product => {
      const frameTag = product.tags?.find(tag => tag.includes('frame_'));
      if (frameTag) {
        const frameGroup = frameTag.split('~')[0].split('_')[1];
        if (!framesByGroupMap.has(frameGroup)) {
          framesByGroupMap.set(frameGroup, product);
        }
      }
    });

    const framesByGroup = Array.from(framesByGroupMap).map(([key, value]) => ({
      frameGroup: key,
      defaultFrameGroupProduct: value,
      image: value.media?.[0]?.src || null,
      allFrames: Object.values(shopProducts).filter(p => {
        const tag = p.tags?.find(t => t.includes('frame_'));
        return tag && tag.split('~')[0].split('_')[1] === key;
      })
    }));

    this.state.set('framesByGroup', framesByGroup);
  }

  setActiveTags(tags) {
    if (!Array.isArray(tags)) return;

    const activeTags = this.state.get('activeTags');
    tags.forEach(tag => {
      const [key] = tag.split('_');
      if (key in activeTags) {
        activeTags[key] = tag;
      }
    });
    this.state.set('activeTags', activeTags);
  }

  handleFrameChange({ product }) {
    const frameTag = product.tags?.find(tag => tag.includes('basecurve_')) || 'DEFAULT';

    this.state.setState({
      product,
      selectedVariant: product.variants.find(v => v.available) || product.variants[0],
      productOptions: product.productOptions,
      frameTagForGettingCorrectRxLensProduct: frameTag
    });

    // Update RX lens product for new frame (using current vision type)
    const rxVisionType = this.state.get('rxVisionType');

    if (rxVisionType?.products?.[frameTag]) {
      const rxLensProduct = rxVisionType.products[frameTag];
      this.state.setState({
        rxLensProduct,
        rxLensSelectedVariant: this.findVariantByOptions(rxLensProduct, this.state.get('rxLensSelectedVariant')?.options || []),
        rxLensProductOptions: rxLensProduct.productOptions
      });
    }

    this.updateGalleryImages();
    this.updateFrameHeader();
    this.updateUrl();
  }

  // Frame name/color in the header are server-rendered from the frame_ tag;
  // keep them in sync when the frame is switched client-side.
  updateFrameHeader() {
    const product = this.state.get('product');
    const frameTag = product?.tags?.find(tag => tag.includes('frame_')) || '';
    const nameEl = this.querySelector('[data-frame-name]');
    const colorEl = this.querySelector('[data-frame-color]');

    if (nameEl) nameEl.textContent = frameTag.replace('frame_', '').split('~')[0];
    if (colorEl) colorEl.textContent = frameTag.split('~').pop();
  }

  handleLensTypeChange({ lensCategory, visionTypes }) {
    // lensCategory is the full rxLensProducts entry with visionTypes
    // visionTypes is the array of vision types for this lens category

    const frameTag = this.state.get('frameTagForGettingCorrectRxLensProduct');
    const firstVisionType = visionTypes[0];
    const product = firstVisionType?.products?.[frameTag];

    if (!product) return;

    const eyewear = product.tags?.find(tag => tag.includes('eyewear_'));
    const activeTags = this.state.get('activeTags');
    activeTags.eyewear = eyewear;

    this.state.setState({
      activeTags,
      rxVisionTypes: visionTypes,
      rxVisionType: firstVisionType,
      rxStyle: this.deriveRxStyle(firstVisionType),
      rxLensProduct: product,
      rxLensSelectedVariant: product.variants[0],
      rxLensProductOptions: product.productOptions
    });

    this.updateGalleryImages();
  }

  handleVisionTypeChange({ visionType, rxStyle }) {
    // visionType is the selected vision type { label, products }
    const frameTag = this.state.get('frameTagForGettingCorrectRxLensProduct');
    const product = visionType?.products?.[frameTag];

    if (!product) return;

    this.state.setState({
      rxVisionType: visionType,
      rxStyle: rxStyle || this.deriveRxStyle(visionType),
      rxLensProduct: product,
      rxLensSelectedVariant: product.variants[0],
      rxLensProductOptions: product.productOptions
    });

    this.updateGalleryImages();
  }

  handleLensOptionChange({ optionName, value }) {
    const rxLensProduct = this.state.get('rxLensProduct');
    const currentVariant = this.state.get('rxLensSelectedVariant');

    // Find matching variant
    const optionIndex = rxLensProduct.productOptions.findIndex(o => o.name === optionName);
    if (optionIndex === -1) return;

    const newOptions = [...currentVariant.options];
    newOptions[optionIndex] = value;

    const newVariant = this.findVariantByOptions(rxLensProduct, newOptions);
    if (newVariant) {
      this.state.set('rxLensSelectedVariant', newVariant);
      this.updateGalleryImages();
    }
  }

  findVariantByOptions(product, options) {
    return product.variants.find(v =>
      v.options.join() === options.join()
    ) || product.selectedVariant || product.variants[0];
  }

  updateGalleryImages() {
    const product = this.state.get('product');
    const rxLensProduct = this.state.get('rxLensProduct');
    const rxLensSelectedVariant = this.state.get('rxLensSelectedVariant');

    if (!product || !rxLensProduct) return;

    const media = product.media?.filter(item => item.media_type === 'image') || [];
    const eyewear = rxLensProduct.tags?.find(tag => tag.includes('eyewear_'));
    const rxLensCategories = window.rxLensCategories || {};

    let targetAlt = eyewear;

    if (eyewear === rxLensCategories.sunglasses) {
      const lensFilterOption = rxLensProduct.productOptions?.find(o => o.name.toLowerCase() === 'lens filter color');
      const mirrorOption = rxLensProduct.productOptions?.find(o => o.name.toLowerCase() === 'mirror');

      if (lensFilterOption && mirrorOption && rxLensSelectedVariant) {
        const lensFilterValue = rxLensSelectedVariant.options[lensFilterOption.position - 1];
        const mirrorValue = rxLensSelectedVariant.options[mirrorOption.position - 1];
        targetAlt = mirrorValue === 'No Mirror'
          ? `${eyewear}~${lensFilterValue}`
          : `${eyewear}~${mirrorValue}`;
      }
    } else if (eyewear === rxLensCategories.photochromic) {
      const lensFilterOption = rxLensProduct.productOptions?.find(o => o.name.toLowerCase() === 'lens filter color');
      if (lensFilterOption && rxLensSelectedVariant) {
        const lensFilterValue = rxLensSelectedVariant.options[lensFilterOption.position - 1];
        targetAlt = `${eyewear}~${lensFilterValue}`;
      }
    }

    const filteredImages = media.filter(img => img.alt === targetAlt);
    this.state.set('images', filteredImages.length > 0 ? filteredImages : media);
  }

  updateUrl() {
    const product = this.state.get('product');
    const selectedVariant = this.state.get('selectedVariant');

    if (!product || !selectedVariant) return;

    const url = new URL(window.location.href);
    url.pathname = `/products/${product.handle}`;
    url.searchParams.set('variant', selectedVariant.id);

    history.replaceState(null, '', url.toString());
  }

  openPrescription() {
    this.state.set('isOpenPrescriptionPopup', true);
    document.documentElement.classList.add('prescription-popup-opened');
    document.body.style.overflow = 'hidden';
    window.scrollTo({ top: 0 });
  }

  closePrescription() {
    this.state.set('isOpenPrescriptionPopup', false);
    this.state.set('prescriptionStep', 'INITIAL');
    document.documentElement.classList.remove('prescription-popup-opened');
    document.body.style.overflow = '';
  }

  toggleFramePopup() {
    const isOpen = !this.state.get('isOpenFramePopup');
    this.state.set('isOpenFramePopup', isOpen);

    if (isOpen) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }
}

// ============================================================================
// RX-GALLERY - Product Gallery with Alt Filtering
// ============================================================================

class RxGallery extends RxBaseComponent {
  #swiper = null;

  init() {
    this.render();
  }

  setupListeners() {
    this.onStateChange('images', () => this.render());
  }

  render() {
    const images = this.state.get('images') || [];
    const selectedVariant = this.state.get('selectedVariant');

    // Mobile slider
    const mobileContainer = this.querySelector('[data-mobile-slider]');
    if (mobileContainer) {
      const wrapper = mobileContainer.querySelector('.swiper-wrapper');
      if (wrapper) {
        wrapper.innerHTML = images.map(image => `
          <div class="swiper-slide">
            <div class="rx-gallery__slide">
              <img
                loading="lazy"
                src="${image.src}"
                alt="${image.alt || selectedVariant?.title || ''}"
                class="rx-gallery__image"
              >
            </div>
          </div>
        `).join('');

        this.initSwiper(mobileContainer);
      }
    }

    // Desktop grid
    const desktopContainer = this.querySelector('[data-desktop-grid]');
    if (desktopContainer) {
      desktopContainer.innerHTML = images.map((image, index) => `
        <div class="rx-gallery__grid-item ${index === 0 ? 'rx-gallery__grid-item--featured' : ''}">
          <div class="rx-gallery__grid-image-wrapper">
            <img
              class="rx-gallery__image"
              loading="lazy"
              src="${image.src}"
              alt="${image.alt || ''}"
            >
          </div>
        </div>
      `).join('');
    }
  }

  initSwiper(container) {
    if (this.#swiper) {
      this.#swiper.destroy(true, true);
    }

    if (window.Swiper) {
      this.#swiper = new window.Swiper(container, {
        observer: true,
        resizeObserver: true,
        scrollbar: {
          el: '.swiper-scrollbar',
          draggable: true,
          snapOnRelease: true
        }
      });
    }
  }

  cleanup() {
    this.#swiper?.destroy(true, true);
  }
}

// ============================================================================
// RX-LENS-SELECTOR - Lens Type Tabs (Clear/Sunglasses/Photochromic)
// ============================================================================

class RxLensSelector extends RxBaseComponent {
  init() {
    // Delay initial render to allow data to load
    requestAnimationFrame(() => this.render());
  }

  setupListeners() {
    // Re-render when rxLensProducts or rxLensProduct changes
    this.onStateChange('rxLensProducts', () => this.render());
    this.onStateChange('rxLensProduct', () => this.render());

    this.listen(this, 'click', (e) => {
      const button = e.target.closest('[data-lens-category-index]');
      if (!button) return;

      const categoryIndex = parseInt(button.dataset.lensCategoryIndex, 10);
      const rxLensProducts = this.state.get('rxLensProducts') || [];
      const lensCategory = rxLensProducts[categoryIndex];

      if (lensCategory?.visionTypes) {
        this.emit('rx:lens-type-change', {
          lensCategory,
          visionTypes: lensCategory.visionTypes
        });
      }
    });
  }

  render() {
    const rxLensProducts = this.state.get('rxLensProducts') || [];
    const rxLensProduct = this.state.get('rxLensProduct');
    const frameTag = this.state.get('frameTagForGettingCorrectRxLensProduct') || 'DEFAULT';

    // Hide if no lens products or only one
    if (rxLensProducts.length <= 1) {
      this.hidden = true;
      this.innerHTML = '';
      return;
    }

    // Filter categories that have valid product for current frame tag in any vision type
    const validCategories = rxLensProducts.filter(p =>
      p.visionTypes?.some(vt => vt.products?.[frameTag])
    );

    if (validCategories.length <= 1) {
      this.hidden = true;
      this.innerHTML = '';
      return;
    }

    this.hidden = false;

    this.innerHTML = `
      <div class="rx-lens-selector__tabs" role="tablist" aria-label="Lens type selector">
        ${validCategories.map((category, index) => {
          // Check if current rxLensProduct belongs to this category
          const isActive = category.visionTypes?.some(vt =>
            vt.products?.[frameTag]?.id === rxLensProduct?.id
          );

          return `
            <button
              type="button"
              class="rx-lens-selector__tab"
              role="tab"
              data-lens-category-index="${rxLensProducts.indexOf(category)}"
              aria-label="Switch eyewear to ${category.title}"
              aria-selected="${isActive}"
            >
              ${category.title}
            </button>
          `;
        }).join('')}
      </div>
    `;
  }
}

// ============================================================================
// RX-VISION-SELECTOR - Vision Type Tabs (Single Vision / Progressive)
// ============================================================================

class RxVisionSelector extends RxBaseComponent {
  init() {
    requestAnimationFrame(() => this.render());
  }

  setupListeners() {
    // Re-render when vision types change
    this.onStateChange('rxVisionTypes', () => this.render());
    this.onStateChange('rxVisionType', () => this.render());
    this.onStateChange('rxStyle', () => this.render());

    this.listen(this, 'click', (e) => {
      const button = e.target.closest('[data-vision-tab]');
      if (!button) return;

      const rxVisionTypes = this.state.get('rxVisionTypes') || [];
      const visionTypeIndex = parseInt(button.dataset.visionTypeIndex, 10);
      const rxStyle = button.dataset.rxStyle;
      const visionType = rxVisionTypes[visionTypeIndex];

      if (visionType) {
        this.emit('rx:vision-type-change', { visionType, rxStyle });
      }
    });
  }

  buildTabs() {
    const rxVisionTypes = this.state.get('rxVisionTypes') || [];
    const frameTag = this.state.get('frameTagForGettingCorrectRxLensProduct') || 'DEFAULT';
    const splitSvReaders = this.state.get('splitSvReaders');

    // Filter vision types that have valid product for current frame tag
    const validVisionTypes = rxVisionTypes.filter(vt => vt.products?.[frameTag]);

    const tabs = [];

    for (let i = 0; i < validVisionTypes.length; i++) {
      const vt = validVisionTypes[i];
      const originalIndex = rxVisionTypes.indexOf(vt);
      const derivedStyle = this.getRxProductPage().deriveRxStyle(vt);

      if (splitSvReaders && derivedStyle === 'SV') {
        // Split into SV + Readers — both point to same vision type and products
        tabs.push({ label: 'Single Vision', visionTypeIndex: originalIndex, rxStyle: 'SV' });
        tabs.push({ label: 'Readers', visionTypeIndex: originalIndex, rxStyle: 'Readers' });
      } else {
        tabs.push({ label: vt.label, visionTypeIndex: originalIndex, rxStyle: derivedStyle });
      }
    }

    return tabs;
  }

  getRxProductPage() {
    return this.closest('rx-product-page');
  }

  render() {
    const rxVisionType = this.state.get('rxVisionType');
    const currentRxStyle = this.state.get('rxStyle');

    const tabs = this.buildTabs();

    // Hide if fewer than 2 tabs
    if (tabs.length <= 1) {
      this.hidden = true;
      this.innerHTML = '';
      return;
    }

    this.hidden = false;

    this.innerHTML = `
      <div class="rx-vision-selector__tabs" role="tablist" aria-label="Vision type selector">
        ${tabs.map(tab => {
          const rxVisionTypes = this.state.get('rxVisionTypes') || [];
          const vt = rxVisionTypes[tab.visionTypeIndex];
          const isActive = vt?.label === rxVisionType?.label && tab.rxStyle === currentRxStyle;

          return `
            <button
              type="button"
              class="rx-vision-selector__tab"
              role="tab"
              data-vision-tab
              data-vision-type-index="${tab.visionTypeIndex}"
              data-rx-style="${tab.rxStyle}"
              aria-label="Switch vision type to ${tab.label}"
              aria-selected="${isActive}"
            >
              ${tab.label}
            </button>
          `;
        }).join('')}
      </div>
    `;
  }
}

// ============================================================================
// RX-LENS-OPTIONS - Lens Option Buttons (Material, Type)
// ============================================================================

class RxLensOptions extends RxBaseComponent {
  init() {
    this.render();
  }

  setupListeners() {
    this.onStateChange('rxLensProductOptions', () => this.render());
    this.onStateChange('rxLensSelectedVariant', () => this.render());

    this.listen(this, 'click', (e) => {
      const button = e.target.closest('[data-option-value]');
      if (!button) return;

      const optionName = button.dataset.optionName;
      const value = button.dataset.optionValue;

      this.emit('rx:lens-option-change', { optionName, value });
    });
  }

  render() {
    const options = this.state.get('rxLensProductOptions') || [];
    const selectedVariant = this.state.get('rxLensSelectedVariant');
    const rxLensProduct = this.state.get('rxLensProduct');
    const frameTag = this.state.get('frameTagForGettingCorrectRxLensProduct');

    // Filter out color options (handled by rx-color-picker)
    const filteredOptions = options.filter(o =>
      !['lens filter color', 'mirror'].includes(o.name.toLowerCase())
    );

    this.innerHTML = filteredOptions.map(option => `
      <div class="rx-lens-options__group">
        <div class="rx-lens-options__label">
          <span>${option.name}</span>
        </div>
        <div class="rx-lens-options__grid">
          ${option.values.map(value => {
            const isSelected = selectedVariant?.options.includes(value);
            const extensionKey = `${rxLensProduct?.tags?.find(t => t.includes('eyewear_'))}-${value}-${frameTag}`;
            const extension = window.extensionOption?.[extensionKey];

            return `
              <button
                type="button"
                class="rx-lens-options__button"
                data-option-name="${option.name}"
                data-option-value="${value}"
                aria-label="Product option ${value}"
                aria-selected="${isSelected}"
              >
                <div class="rx-lens-options__button-content">
                  <span class="rx-lens-options__button-title">${value}</span>
                  ${extension?.description ? `<span class="rx-lens-options__button-description">${extension.description}</span>` : ''}
                </div>
                ${extension?.extra_price ? `
                  <span class="rx-lens-options__button-price">
                    ${option.name.toLowerCase() !== 'lens type' ? '+' : ''}${RxUtils.formatMoney(extension.extra_price, true).replace('.00', '')}
                  </span>
                ` : ''}
              </button>
            `;
          }).join('')}
        </div>
      </div>
    `).join('');
  }
}

// ============================================================================
// RX-COLOR-PICKER - Dropdown Color Selector
// ============================================================================

class RxColorPicker extends RxBaseComponent {
  #isOpen = false;

  get optionName() {
    return this.getAttribute('option-name') || '';
  }

  init() {
    this.render();
  }

  setupListeners() {
    this.onStateChange('rxLensSelectedVariant', () => this.render());
    this.onStateChange('rxLensProductOptions', () => this.render());

    this.listen(this, 'click', (e) => {
      const toggle = e.target.closest('[data-toggle]');
      if (toggle) {
        e.stopPropagation();
        this.#isOpen = !this.#isOpen;
        this.render();
        return;
      }

      const option = e.target.closest('[data-color-value]');
      if (option) {
        e.stopPropagation();
        this.emit('rx:lens-option-change', {
          optionName: this.optionName,
          value: option.dataset.colorValue
        });
        this.#isOpen = false;
        this.render();
      }
    });

    // Close on outside click
    this.listen(document, 'click', (e) => {
      if (this.#isOpen && !this.contains(e.target)) {
        this.#isOpen = false;
        this.render();
      }
    });

    // Close on backdrop click (mobile)
    this.listen(this, 'click', (e) => {
      if (e.target.closest('[data-backdrop]')) {
        this.#isOpen = false;
        this.render();
      }
    });

    // Close on Escape key
    this.listen(document, 'keydown', (e) => {
      if (e.key === 'Escape' && this.#isOpen) {
        this.#isOpen = false;
        this.render();
      }
    });
  }

  render() {
    const options = this.state.get('rxLensProductOptions') || [];
    const selectedVariant = this.state.get('rxLensSelectedVariant');

    const option = options.find(o => o.name.toLowerCase() === this.optionName.toLowerCase());
    if (!option || option.values.length === 1) {
      this.hidden = true;
      this.removeAttribute('open');
      return;
    }

    this.hidden = false;
    this.toggleAttribute('open', this.#isOpen);

    const currentValue = selectedVariant?.options[option.position - 1] || option.values[0];
    const colorSwatch = window.colorSwatch || {};

    this.innerHTML = `
      ${this.#isOpen ? `<div class="rx-color-picker__backdrop" data-backdrop></div>` : ''}
      <div class="rx-color-picker__wrapper">
        <label class="rx-color-picker__label">${option.name}</label>
        <button
          type="button"
          class="rx-color-picker__trigger"
          data-toggle
          aria-expanded="${this.#isOpen}"
          aria-haspopup="listbox"
        >
          <span class="rx-color-picker__swatch" data-color-swatch="${currentValue}" title="${currentValue}"></span>
          <span class="rx-color-picker__value">${currentValue}</span>
          ${colorSwatch[currentValue]?.plus_price ? `
            <span class="rx-color-picker__price">+${RxUtils.formatMoney(colorSwatch[currentValue].plus_price).replace('.00', '')}</span>
          ` : ''}
          <svg class="rx-color-picker__chevron" width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        ${this.#isOpen ? `
          <div class="rx-color-picker__dropdown" role="listbox" aria-label="${option.name} options">
            ${option.values.map(value => {
              const isSelected = value === currentValue;
              return `
                <button
                  type="button"
                  class="rx-color-picker__option"
                  role="option"
                  aria-selected="${isSelected}"
                  data-color-value="${value}"
                >
                  <span class="rx-color-picker__swatch" data-color-swatch="${value}" title="${value}"></span>
                  <span class="rx-color-picker__value">${value}</span>
                  ${colorSwatch[value]?.plus_price ? `
                    <span class="rx-color-picker__price">+${RxUtils.formatMoney(colorSwatch[value].plus_price).replace('.00', '')}</span>
                  ` : ''}
                </button>
              `;
            }).join('')}
          </div>
        ` : ''}
      </div>
    `;
  }
}

// ============================================================================
// RX-FRAME-SELECTOR - Frame Color Grid (uses alternateProducts metafield)
// ============================================================================

class RxFrameSelector extends RxBaseComponent {
  init() {
    this.render();
  }

  setupListeners() {
    this.onStateChange('product', () => this.render());

    this.listen(this, 'click', async (e) => {
      const button = e.target.closest('[data-frame-handle]');
      if (!button) return;

      const frameHandle = button.dataset.frameHandle;
      const frameId = button.dataset.frameId;
      const currentProduct = this.state.get('product');

      // Don't do anything if clicking the current product
      if (frameId && parseInt(frameId) === currentProduct?.id) return;

      if (frameHandle) {
        await this.loadAndSwitchProduct(frameHandle);
      }
    });
  }

  async loadAndSwitchProduct(handle) {
    try {
      // Show loading state
      this.classList.add('is-loading');

      // Fetch product data from Shopify JSON endpoint
      const response = await fetch(`/products/${handle}.json`);
      if (!response.ok) throw new Error('Failed to fetch product');

      const data = await response.json();
      const newProduct = this.transformShopifyProduct(data.product);

      // Emit frame change event with full product data
      // Note: handleFrameChange will call updateUrl() to update the browser URL
      this.emit('rx:frame-change', { product: newProduct });

    } catch (error) {
      console.error('RxFrameSelector: Failed to load product:', error);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  transformShopifyProduct(shopifyProduct) {
    // Transform Shopify product JSON to match our expected format
    return {
      id: shopifyProduct.id,
      handle: shopifyProduct.handle,
      title: shopifyProduct.title,
      description: shopifyProduct.body_html,
      type: shopifyProduct.product_type,
      tags: shopifyProduct.tags ? shopifyProduct.tags.split(', ') : [],
      price: shopifyProduct.variants?.[0]?.price ? parseInt(shopifyProduct.variants[0].price * 100) : 0,
      compare_at_price: shopifyProduct.variants?.[0]?.compare_at_price ? parseInt(shopifyProduct.variants[0].compare_at_price * 100) : null,
      media: shopifyProduct.images?.map(img => ({
        id: img.id,
        media_type: 'image',
        alt: img.alt,
        src: img.src,
        width: img.width,
        height: img.height
      })) || [],
      variants: shopifyProduct.variants?.map(v => ({
        id: v.id,
        title: v.title,
        sku: v.sku,
        available: v.available,
        price: v.price ? parseInt(v.price * 100) : 0,
        compare_at_price: v.compare_at_price ? parseInt(v.compare_at_price * 100) : null,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3,
        options: [v.option1, v.option2, v.option3].filter(Boolean),
        image: v.featured_image?.src || null
      })) || [],
      productOptions: shopifyProduct.options?.map(opt => ({
        name: opt.name,
        values: opt.values
      })) || [],
      // Keep alternate products from current product (they should be the same group)
      alternateProducts: this.state.get('product')?.alternateProducts || []
    };
  }

  render() {
    const product = this.state.get('product');

    if (!product) return;

    // Get alternate products from metafield (passed via rx-product-data.liquid)
    const alternateProducts = product.alternateProducts || [];

    // If no alternate products, hide the component
    if (alternateProducts.length === 0) {
      this.innerHTML = '';
      this.hidden = true;
      return;
    }

    this.hidden = false;

    this.innerHTML = `
      <div class="rx-frame-selector__wrapper">
        <div class="rx-frame-selector__label">
          <span class="rx-frame-selector__label-text">Frame color:</span>
          <span class="rx-frame-selector__label-value">${this.getFrameColor(product)}</span>
        </div>
        <div class="rx-frame-selector__grid">
          ${alternateProducts.map(frame => `
            <button
              type="button"
              class="rx-frame-selector__item${frame.id === product.id ? ' is-selected' : ''}"
              data-frame-handle="${frame.handle}"
              data-frame-id="${frame.id}"
              aria-label="Select ${frame.title} frame"
              aria-selected="${frame.id === product.id}"
            >
              <img
                loading="lazy"
                src="${frame.image || ''}"
                alt="${frame.title}"
              >
            </button>
          `).join('')}
        </div>
      </div>
    `;
  }

  getFrameColor(product) {
    const frameTag = product?.tags?.find(t => t.includes('frame_'));
    return frameTag?.split('~')[1] || '';
  }
}

// ============================================================================
// RX-FRAME-POPUP - Frame Group Selection Popup
// ============================================================================

class RxFramePopup extends RxBaseComponent {
  init() {
    this.render();
  }

  setupListeners() {
    this.onStateChange('isOpenFramePopup', () => this.render());
    this.onStateChange('framesByGroup', () => this.render());

    this.listen(this, 'click', (e) => {
      const frame = e.target.closest('[data-frame-group]');
      if (!frame) return;

      const frameGroup = frame.dataset.frameGroup;
      const framesByGroup = this.state.get('framesByGroup');
      const group = framesByGroup.find(g => g.frameGroup === frameGroup);

      if (group?.defaultFrameGroupProduct) {
        this.emit('rx:frame-change', { product: group.defaultFrameGroupProduct });
        this.emit('rx:frame-popup-toggle');
      }
    });
  }

  render() {
    const isOpen = this.state.get('isOpenFramePopup');
    const framesByGroup = this.state.get('framesByGroup') || [];
    const product = this.state.get('product');

    const frameTag = product?.tags?.find(t => t.includes('frame_'));
    const currentFrameGroup = frameTag?.split('~')[0].split('_')[1];

    this.hidden = !isOpen;
    if (!isOpen) return;

    this.innerHTML = `
      <div class="rx-frame-popup__overlay">
        <div class="rx-frame-popup__grid rx-scrollbar-hide">
          ${framesByGroup.map(frame => `
            <button
              type="button"
              class="rx-frame-popup__item"
              data-frame-group="${frame.frameGroup}"
              aria-selected="${frame.frameGroup === currentFrameGroup}"
              aria-label="Select ${frame.frameGroup} frame group"
            >
              <div class="rx-frame-popup__item-image">
                <img loading="lazy" src="${frame.image || ''}" alt="${frame.frameGroup}">
              </div>
              <span class="rx-frame-popup__item-title">${frame.frameGroup}</span>
            </button>
          `).join('')}
        </div>
      </div>
    `;
  }
}

// ============================================================================
// RX-PRESCRIPTION-DRAWER - Prescription Form Drawer
// ============================================================================

class RxPrescriptionDrawer extends RxBaseComponent {
  init() {
    this.render();
  }

  setupListeners() {
    this.onStateChange('isOpenPrescriptionPopup', () => this.render());
    this.onStateChange('prescriptionStep', () => this.render());
    this.onStateChange('isAnalyzing', () => this.render());

    this.listen(this, 'click', (e) => {
      if (e.target.closest('[data-close]')) {
        this.emit('rx:prescription-close');
      }

      if (e.target.closest('[data-return]')) {
        this.state.set('prescriptionStep', 'INITIAL');
        // Reset file-related state when returning
        this.state.set('prescriptionFile', null);
        this.state.set('prescriptionFilePath', null);
      }

      // Upload button - trigger hidden file input immediately
      if (e.target.closest('[data-upload-trigger]')) {
        const fileInput = this.querySelector('#rx-file-input');
        if (fileInput) {
          fileInput.click();
        }
      }

      if (e.target.closest('[data-step]')) {
        const step = e.target.closest('[data-step]').dataset.step;
        this.state.set('prescriptionStep', step);
      }

      if (e.target.closest('[data-checkout]')) {
        this.handleCheckout();
      }

      // Submit form from footer button
      if (e.target.closest('[data-submit-form]')) {
        this.handleCheckout();
      }
    });

    // File input change handler
    this.listen(this, 'change', async (e) => {
      if (e.target.id === 'rx-file-input' && e.target.files[0]) {
        await this.handleFileUpload(e.target.files[0]);
      }
    });

    // Listen for manual form submission to trigger checkout
    this.listen(this, 'rx:manual-form-submit', () => {
      this.handleCheckout();
    });
  }

  render() {
    const isOpen = this.state.get('isOpenPrescriptionPopup');
    const step = this.state.get('prescriptionStep');
    const isAnalyzing = this.state.get('isAnalyzing');

    this.hidden = !isOpen;
    if (!isOpen) {
      this.innerHTML = '';
      return;
    }

    const selectedVariant = this.state.get('selectedVariant');
    const rxLensSelectedVariant = this.state.get('rxLensSelectedVariant');
    const totalPrice = (selectedVariant?.price || 0) + (rxLensSelectedVariant?.price || 0);

    this.innerHTML = `
      <div class="rx-prescription-drawer__overlay">
        <!-- Hidden file input - triggers immediately on upload button click -->
        <input type="file" id="rx-file-input" hidden accept="image/*,.pdf">

        ${isAnalyzing ? this.renderLoader() : ''}

        <div class="rx-prescription-drawer__header">
          ${step !== 'INITIAL' ? `
            <button type="button" class="rx-prescription-drawer__back" data-return>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M11.2323 2.26414C11.4037 2.43332 11.5 2.66274 11.5 2.90196C11.5 3.14118 11.4037 3.37061 11.2323 3.53978L6.7068 8.00545L11.2323 12.4711C11.3989 12.6413 11.491 12.8691 11.4889 13.1057C11.4868 13.3422 11.3907 13.5685 11.2212 13.7358C11.0517 13.903 10.8224 13.9979 10.5827 14C10.3429 14.002 10.112 13.9111 9.93958 13.7468L4.76768 8.64327C4.59628 8.47409 4.5 8.24467 4.5 8.00545C4.5 7.76623 4.59628 7.5368 4.76768 7.36762L9.93958 2.26414C10.111 2.09501 10.3435 2 10.5859 2C10.8284 2 11.0609 2.09501 11.2323 2.26414Z"/></svg>
              <span>return</span>
            </button>
          ` : ''}
          <button type="button" class="rx-prescription-drawer__close" data-close>
            <span class="rx-sr-only">Close prescription popup</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        <div class="rx-prescription-drawer__content">
          ${this.renderStep(step)}
        </div>

        ${step === 'MANUAL' ? `
          <div class="rx-prescription-drawer__footer">
            <div class="rx-prescription-drawer__footer-summary">
              <span>Total</span>
              <span class="rx-prescription-drawer__footer-price">${RxUtils.formatMoney(totalPrice)}</span>
            </div>
            <button type="button" class="rx-prescription-drawer__footer-btn" data-submit-form>
              <span>Add to Cart</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </button>
          </div>
        ` : ''}
      </div>
    `;
  }

  renderLoader() {
    return `
      <div class="rx-prescription-drawer__loader">
        <div class="rx-loader">
          <svg class="rx-loader__spinner" viewBox="0 0 50 50">
            <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="3"></circle>
          </svg>
        </div>
        <p class="rx-prescription-drawer__loader-text">Analyzing your prescription...</p>
      </div>
    `;
  }

  renderStep(step) {
    switch (step) {
      case 'MANUAL':
        return this.renderManualStep();
      default:
        return this.renderInitialStep();
    }
  }

  renderInitialStep() {
    const selectedVariant = this.state.get('selectedVariant');
    const rxLensSelectedVariant = this.state.get('rxLensSelectedVariant');
    const totalPrice = (selectedVariant?.price || 0) + (rxLensSelectedVariant?.price || 0);

    return `
      <div class="rx-prescription-drawer__step rx-prescription-drawer__step--initial">
        <div class="rx-prescription-drawer__step-header">
          <h2 class="rx-prescription-drawer__title">Do you have a prescription?</h2>
          <p class="rx-prescription-drawer__subtitle">We require a valid Doctor's prescription</p>
        </div>

        <div class="rx-prescription-drawer__step-actions">
          <button type="button" class="btn atc button button--full-width button--primary" data-upload-trigger>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
            <span>${RxUtils.titleCase('upload prescription')}</span>
          </button>

          <span class="rx-prescription-drawer__or">OR</span>

          <button type="button" class="btn atc button button--full-width button--primary" data-step="MANUAL">
            <span>${RxUtils.titleCase('input it manually')}</span>
          </button>
        </div>

        <div class="rx-prescription-drawer__step-summary">
          <span class="rx-prescription-drawer__subtitle">SUBTOTAL:</span>
          <span class="rx-prescription-drawer__title">${RxUtils.formatMoney(totalPrice)}</span>
        </div>

        <div class="rx-prescription-drawer__step-checkout">
          <button type="button" class="btn atc button button--full-width button--primary" data-checkout>
            <span>${RxUtils.titleCase('CHECKOUT')}</span>
          </button>
          <span class="rx-prescription-drawer__subtitle">SEND PRESCRIPTION LATER</span>
        </div>
      </div>
    `;
  }

  renderManualStep() {
    const prescriptionFile = this.state.get('prescriptionFile');
    const prescriptionFilePath = this.state.get('prescriptionFilePath');

    return `
      <div class="rx-prescription-drawer__step">
        <div class="rx-prescription-drawer__step-header">
          <h2 class="rx-prescription-drawer__title">${prescriptionFile ? 'Confirm your prescription' : 'Enter your prescription'}</h2>
        </div>

        ${prescriptionFile ? `
          <div class="rx-file-upload__preview" style="margin-bottom: 1.5rem;">
            <div class="rx-file-upload__preview-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
              <span class="rx-file-upload__preview-name">${prescriptionFile.name} (${(prescriptionFile.size / 1024 / 1024).toFixed(2)}mb)</span>
            </div>
            ${prescriptionFilePath ? `
              <div class="rx-file-upload__preview-image">
                <img src="${prescriptionFilePath}" alt="Uploaded prescription">
              </div>
            ` : ''}
          </div>
        ` : ''}

        <rx-manual-form></rx-manual-form>
      </div>
    `;
  }

  async handleFileUpload(file) {
    if (!file) return;

    // Show loader
    this.state.set('isAnalyzing', true);
    this.state.set('prescriptionFile', file);

    // Create preview for images
    let filePath = null;
    if (file.type.startsWith('image/')) {
      filePath = URL.createObjectURL(file);
      this.state.set('prescriptionFilePath', filePath);
    }

    try {
      // Analyze with ScriptAI
      const result = await RxUtils.analyzePrescription(file);

      // Save prescription data
      if (result) {
        this.state.set('prescriptionData', result.prescriptionData);
        this.state.set('prescriptionUniqueId', result.uniqueId);

        // Also save rxUID if returned
        if (result.rxUID) {
          this.state.set('prescriptionRxUID', result.rxUID);
        }

        // Fill manual form with extracted data
        if (result.prescriptionData) {
          this.fillFormFromAnalysis(result);
        }
      }

      // Go to manual step with pre-filled data
      this.state.set('prescriptionStep', 'MANUAL');

    } catch (error) {
      console.error('Analysis failed:', error);
      // Still go to manual step but with empty form
      this.state.set('prescriptionStep', 'MANUAL');
    } finally {
      this.state.set('isAnalyzing', false);
    }
  }

  fillFormFromAnalysis(result) {
    const formField = { ...this.state.get('manualFormField') };
    const data = result.prescriptionData || {};
    const details = result.analysis?.consensus?.prescriptionDetails || {};

    // Map ScriptAI response to form fields
    if (data.sphRight) formField.SPH.R = data.sphRight;
    if (data.sphLeft) formField.SPH.L = data.sphLeft;
    if (data.cylRight) formField.CYL.R = data.cylRight;
    if (data.cylLeft) formField.CYL.L = data.cylLeft;
    if (data.axisRight) formField.AXIS.R = String(data.axisRight).padStart(3, '0');
    if (data.axisLeft) formField.AXIS.L = String(data.axisLeft).padStart(3, '0');
    if (data.addRight) formField.ADD.R = data.addRight;
    if (data.addLeft) formField.ADD.L = data.addLeft;

    // PD values
    if (data.isMonocularPd && data.pdRight && data.pdLeft) {
      formField.PUPIL.R = data.pdRight;
      formField.PUPIL.L = data.pdLeft;
      this.state.set('isOpenPD', true);
    } else if (data.pupillaryDistance) {
      formField.PUPIL_RL.RL = data.pupillaryDistance;
    }

    // Prism values - map from analysis.consensus.prescriptionDetails
    const mapPrismDirection = (base) => {
      if (!base) return null;
      const map = { 'BO': 'O', 'BI': 'I', 'BU': 'U', 'BD': 'D' };
      return map[base.toUpperCase()] || base.charAt(base.length - 1).toUpperCase();
    };

    const rightEye = details.rightEye || {};
    const leftEye = details.leftEye || {};
    let hasPrism = false;

    // Right eye prism
    if (rightEye.prismHorizontal) {
      formField.PRISM.R.horizontal.value = rightEye.prismHorizontal;
      formField.PRISM.R.horizontal.direction = mapPrismDirection(rightEye.baseHorizontal) || 'I';
      hasPrism = true;
    }
    if (rightEye.prismVertical) {
      formField.PRISM.R.vertical.value = rightEye.prismVertical;
      formField.PRISM.R.vertical.direction = mapPrismDirection(rightEye.baseVertical) || 'U';
      hasPrism = true;
    }

    // Left eye prism
    if (leftEye.prismHorizontal) {
      formField.PRISM.L.horizontal.value = leftEye.prismHorizontal;
      formField.PRISM.L.horizontal.direction = mapPrismDirection(leftEye.baseHorizontal) || 'I';
      hasPrism = true;
    }
    if (leftEye.prismVertical) {
      formField.PRISM.L.vertical.value = leftEye.prismVertical;
      formField.PRISM.L.vertical.direction = mapPrismDirection(leftEye.baseVertical) || 'U';
      hasPrism = true;
    }

    // Open prism section if prism values were detected
    if (hasPrism) {
      this.state.set('isOpenPrism', true);
    }

    this.state.set('manualFormField', formField);
  }

  async handleCheckout() {
    const product = this.state.get('product');
    const selectedVariant = this.state.get('selectedVariant');
    const rxLensProduct = this.state.get('rxLensProduct');
    const rxLensSelectedVariant = this.state.get('rxLensSelectedVariant');
    const prescriptionStep = this.state.get('prescriptionStep');
    const prescriptionUniqueId = this.state.get('prescriptionUniqueId');
    let prescriptionRxUID = this.state.get('prescriptionRxUID');
    const manualFormField = this.state.get('manualFormField');
    const isOpenPD = this.state.get('isOpenPD');
    const rxVisionType = this.state.get('rxVisionType');
    const rxStyle = this.state.get('rxStyle');

    // Validate ADD field for Progressive/Multifocal/Bifocal lenses.
    // Only applies when the customer is entering the prescription now (MANUAL step);
    // "Send prescription later" (INITIAL step) has no form to validate.
    const visionTypeLabel = (rxVisionType?.label || '').toLowerCase();
    const isAddRequired = prescriptionStep === 'MANUAL' && (
      visionTypeLabel.includes('progressive') ||
      visionTypeLabel.includes('multifocal') ||
      visionTypeLabel.includes('bifocal')
    );

    if (isAddRequired) {
      const addR = manualFormField.ADD?.R;
      const addL = manualFormField.ADD?.L;
      const hasAddR = addR && addR !== 'Select';
      const hasAddL = addL && addL !== 'Select';

      if (!hasAddR || !hasAddL) {
        alert('ADD values are required for Progressive lenses. Please fill in both ADD fields.');
        // Trigger re-render to show error state
        this.state.set('manualFormField', { ...manualFormField });
        return;
      }
    }

    this.state.set('loading', true);

    try {
      // Save prescription to RX Proxy
      const prescriptionPayload = RxUtils.buildPrescriptionPayload(
        manualFormField, isOpenPD, this.state.get('isOpenPrism')
      );

      try {
        const saveResult = await RxUtils.savePrescription({
          orderId: 'cart-temp-' + Date.now(),
          lineItemId: 'prescription-' + Date.now(),
          prescriptionData: prescriptionPayload
        });

        if (saveResult.success) {
          const rxUID = saveResult.data?.rxUID || saveResult.rxUID;
          if (rxUID) {
            this.state.set('prescriptionRxUID', rxUID);
            prescriptionRxUID = rxUID;
          }
        }
      } catch (saveError) {
        console.error('Failed to save prescription to RX Proxy:', saveError);
        // Continue to cart add even if save fails
      }

      // Determine prescription type
      const prescriptionType = prescriptionUniqueId ? 'file' : (prescriptionStep === 'MANUAL' ? 'manual' : 'pending');

      // Calculate PD values
      let pdValue = null;
      let pdLeft = null;
      let pdRight = null;

      if (isOpenPD) {
        pdLeft = manualFormField.PUPIL.L !== 'Select' ? manualFormField.PUPIL.L : null;
        pdRight = manualFormField.PUPIL.R !== 'Select' ? manualFormField.PUPIL.R : null;
      } else {
        pdValue = manualFormField.PUPIL_RL.RL !== 'Select' ? manualFormField.PUPIL_RL.RL : null;
      }

      // Build properties using utility function
      const baseProperties = RxUtils.buildLineItemProperties({
        product,
        selectedVariant,
        rxLensProduct,
        rxLensSelectedVariant,
        prescriptionType,
        prescriptionUniqueId,
        prescriptionRxUID,
        pdValue,
        pdLeft,
        pdRight,
        isOpenPD,
        rxStyle
      });

      // Generate bundle hash
      const hash = Date.now();
      const _bundleHash = `bundle-hash-${hash}-${rxLensSelectedVariant?.id}-${selectedVariant?.id}`;

      // Lens product with full properties
      const lensProperties = {
        ...baseProperties,
        _bundleHash
      };

      // Frame product with minimal properties
      const frameProperties = {
        _bundleHash,
        'Frame SKU': selectedVariant?.sku || '',
        'Lens SKU': rxLensSelectedVariant?.sku || ''
      };

      const items = [
        {
          id: rxLensSelectedVariant.id,
          quantity: 1,
          properties: lensProperties
        },
        {
          id: selectedVariant.id,
          quantity: 1,
          properties: frameProperties
        }
      ];

      // Collect sections to bundle (Prestige theme pattern)
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items,
          sections: sectionsToBundle.join(',')
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add items to cart');
      }

      const responseJson = await response.json();

      // Close prescription drawer
      this.emit('rx:prescription-close');

      // Fetch updated cart and add sections from response
      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent['sections'] = responseJson['sections'];

      // Dispatch cart:change event - this triggers the cart drawer to open in Prestige theme
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          cart: cartContent,
          items: responseJson.items || [responseJson]
        }
      }));

    } catch (e) {
      console.error('Failed to add to cart:', e);
      alert('Failed to add items to cart. Please try again.');
    } finally {
      this.state.set('loading', false);
    }
  }
}

// ============================================================================
// RX-FILE-UPLOAD - File Upload Component
// DEPRECATED: This component is no longer used. File upload functionality
// has been moved directly into RxPrescriptionDrawer for a streamlined flow.
// Kept for backwards compatibility only.
// ============================================================================

class RxFileUpload extends RxBaseComponent {
  #file = null;
  #filePath = null;
  #isUploading = false;

  init() {
    this.render();
  }

  setupListeners() {
    this.listen(this, 'change', (e) => {
      if (e.target.type === 'file') {
        this.handleFileSelect(e.target.files[0]);
      }
    });

    this.listen(this, 'click', (e) => {
      if (e.target.closest('[data-remove-file]')) {
        this.#file = null;
        this.#filePath = null;
        this.render();
      }

      if (e.target.closest('[data-continue]')) {
        this.handleContinue();
      }
    });

    // Drag and drop
    this.listen(this, 'dragover', (e) => {
      e.preventDefault();
      this.classList.add('drag-over');
    });

    this.listen(this, 'dragleave', () => {
      this.classList.remove('drag-over');
    });

    this.listen(this, 'drop', (e) => {
      e.preventDefault();
      this.classList.remove('drag-over');
      if (e.dataTransfer.files[0]) {
        this.handleFileSelect(e.dataTransfer.files[0]);
      }
    });
  }

  render() {
    if (!this.#file) {
      this.innerHTML = `
        <div class="rx-file-upload__empty">
          <div class="rx-file-upload__icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
          </div>
          <label class="btn atc button button--full-width button--primary">
            <input type="file" name="rx-file" hidden accept="image/*,.pdf">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
            <span>${RxUtils.titleCase('select a file')}</span>
          </label>
          <p class="rx-file-upload__hint">or drag and drop your file here</p>
        </div>
      `;
    } else {
      const fileSize = (this.#file.size / 1024 / 1024).toFixed(2);
      this.innerHTML = `
        <div class="rx-file-upload__success">
          <h3 class="rx-prescription-drawer__title">your prescription was successfully uploaded</h3>

          <button type="button" class="btn atc button button--full-width button--primary" data-continue ${this.#isUploading ? 'aria-busy="true"' : ''}>
            <span>${RxUtils.titleCase('continue')}</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M4.76768 2.26414C4.59628 2.43332 4.5 2.66274 4.5 2.90196C4.5 3.14118 4.59628 3.37061 4.76768 3.53978L9.2932 8.00545L4.76768 12.4711C4.60111 12.6413 4.50899 12.8691 4.51109 13.1057C4.51319 13.3422 4.60932 13.5685 4.77878 13.7358C4.94825 13.903 5.17761 13.9979 5.41729 14C5.65697 14.002 5.88797 13.9111 6.06042 13.7468L11.2323 8.64327C11.4037 8.47409 11.5 8.24467 11.5 8.00545C11.5 7.76623 11.4037 7.5368 11.2323 7.36762L6.06042 2.26414C5.88899 2.09501 5.65649 2 5.41405 2C5.17161 2 4.93912 2.09501 4.76768 2.26414Z"/></svg>
          </button>

          <div class="rx-file-upload__preview">
            <div class="rx-file-upload__preview-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
              <span class="rx-file-upload__preview-name">${this.#file.name} (${fileSize}mb)</span>
              <button type="button" class="rx-file-upload__preview-remove" data-remove-file aria-label="Remove file">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
              </button>
            </div>
            ${this.#filePath ? `
              <div class="rx-file-upload__preview-image">
                <img src="${this.#filePath}" alt="uploaded-image">
              </div>
            ` : ''}
          </div>

          <label class="btn atc button button--full-width button--secondary">
            <input type="file" name="rx-file-change" hidden accept="image/*,.pdf">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
            <span>${RxUtils.titleCase('Change file')}</span>
          </label>

          <rx-pd-input></rx-pd-input>
        </div>
      `;
    }
  }

  async handleFileSelect(file) {
    if (!file) return;

    this.#file = file;
    this.#isUploading = true;
    this.render();

    try {
      // Create preview for images
      if (file.type.startsWith('image/')) {
        this.#filePath = URL.createObjectURL(file);
      }

      // Upload to ScriptAI for analysis
      const result = await RxUtils.analyzePrescription(file);

      // Store prescription data in state
      if (result) {
        this.state.set('prescriptionData', result.prescriptionData);
        this.state.set('prescriptionUniqueId', result.uniqueId);

        // Auto-fill manual form if data was extracted
        if (result.prescriptionData) {
          this.fillFormFromAnalysis(result);
        }
      }

    } catch (e) {
      console.error('Upload failed:', e);
      // Still allow manual entry even if analysis fails
    } finally {
      this.#isUploading = false;
      this.render();
    }
  }

  fillFormFromAnalysis(result) {
    const formField = this.state.get('manualFormField');
    const data = result.prescriptionData || {};
    const details = result.analysis?.consensus?.prescriptionDetails || {};

    // Map ScriptAI response to form fields
    if (data.sphRight) formField.SPH.R = data.sphRight;
    if (data.sphLeft) formField.SPH.L = data.sphLeft;
    if (data.cylRight) formField.CYL.R = data.cylRight;
    if (data.cylLeft) formField.CYL.L = data.cylLeft;
    if (data.axisRight) formField.AXIS.R = String(data.axisRight).padStart(3, '0');
    if (data.axisLeft) formField.AXIS.L = String(data.axisLeft).padStart(3, '0');
    if (data.addRight) formField.ADD.R = data.addRight;
    if (data.addLeft) formField.ADD.L = data.addLeft;

    // PD values
    if (data.isMonocularPd && data.pdRight && data.pdLeft) {
      formField.PUPIL.R = data.pdRight;
      formField.PUPIL.L = data.pdLeft;
      this.state.set('isOpenPD', true);
    } else if (data.pupillaryDistance) {
      formField.PUPIL_RL.RL = data.pupillaryDistance;
    }

    // Prism values - map from analysis.consensus.prescriptionDetails
    const mapPrismDirection = (base) => {
      if (!base) return null;
      const map = { 'BO': 'O', 'BI': 'I', 'BU': 'U', 'BD': 'D' };
      return map[base.toUpperCase()] || base.charAt(base.length - 1).toUpperCase();
    };

    const rightEye = details.rightEye || {};
    const leftEye = details.leftEye || {};
    let hasPrism = false;

    // Right eye prism
    if (rightEye.prismHorizontal) {
      formField.PRISM.R.horizontal.value = rightEye.prismHorizontal;
      formField.PRISM.R.horizontal.direction = mapPrismDirection(rightEye.baseHorizontal) || 'I';
      hasPrism = true;
    }
    if (rightEye.prismVertical) {
      formField.PRISM.R.vertical.value = rightEye.prismVertical;
      formField.PRISM.R.vertical.direction = mapPrismDirection(rightEye.baseVertical) || 'U';
      hasPrism = true;
    }

    // Left eye prism
    if (leftEye.prismHorizontal) {
      formField.PRISM.L.horizontal.value = leftEye.prismHorizontal;
      formField.PRISM.L.horizontal.direction = mapPrismDirection(leftEye.baseHorizontal) || 'I';
      hasPrism = true;
    }
    if (leftEye.prismVertical) {
      formField.PRISM.L.vertical.value = leftEye.prismVertical;
      formField.PRISM.L.vertical.direction = mapPrismDirection(leftEye.baseVertical) || 'U';
      hasPrism = true;
    }

    // Open prism section if prism values were detected
    if (hasPrism) {
      this.state.set('isOpenPrism', true);
    }

    this.state.set('manualFormField', formField);
  }

  handleContinue() {
    this.state.set('prescriptionFile', this.#file);
    this.state.set('prescriptionFilePath', this.#filePath);
    this.emit('rx:file-uploaded', { file: this.#file, filePath: this.#filePath });
  }
}

// ============================================================================
// RX-MANUAL-FORM - Manual Prescription Entry Form
// ============================================================================

class RxManualForm extends RxBaseComponent {
  init() {
    this.render();
  }

  setupListeners() {
    this.onStateChange('manualFormField', () => this.render());
    this.onStateChange('isOpenPD', () => this.render());
    this.onStateChange('isOpenPrism', () => this.render());

    this.listen(this, 'click', (e) => {
      const field = e.target.closest('[data-field]');
      // Don't open drawer for SELECT elements - they handle their own values
      if (field && field.tagName !== 'SELECT') {
        this.emit('rx:open-value-drawer', {
          fieldKey: field.dataset.field,
          eye: field.dataset.eye,
          title: field.dataset.title
        });
      }

      if (e.target.closest('[data-toggle-pd]')) {
        this.state.set('isOpenPD', !this.state.get('isOpenPD'));
      }

      if (e.target.closest('[data-toggle-prism]')) {
        this.state.set('isOpenPrism', !this.state.get('isOpenPrism'));
      }

      if (e.target.closest('[data-measure-pd]')) {
        this.openIframePD();
      }

      if (e.target.closest('[data-submit]')) {
        this.handleSubmit();
      }
    });

    // Listen for PD measurement result from iframe
    window.addEventListener('message', (event) => {
      if (!event?.data?.pd) return;
      const pdValue = String(event.data.pd);
      const formField = this.state.get('manualFormField');
      formField.PUPIL_RL.RL = pdValue;
      if (this.state.get('isOpenPD')) {
        this.state.set('isOpenPD', false);
      }
      this.state.set('manualFormField', { ...formField });
      this.closeIframePD();
    });
  }

  openIframePD() {
    const iframe = document.createElement('div');
    iframe.id = 'pd-iframe';
    iframe.className = 'rx-pd-iframe-overlay';
    iframe.innerHTML = `
      <div class="rx-pd-iframe-container">
        <button type="button" class="rx-pd-iframe-close" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        <iframe
          src="https://pdmeasure.lensync.com/?primaryColor=FFF&textColor=000&lineColor=FFF&borderRadius=3px"
          allow="camera"
        ></iframe>
      </div>
    `;
    document.body.appendChild(iframe);

    // Close on overlay click
    iframe.addEventListener('click', (e) => {
      if (e.target === iframe || e.target.closest('.rx-pd-iframe-close')) {
        this.closeIframePD();
      }
    });
  }

  closeIframePD() {
    const iframe = document.getElementById('pd-iframe');
    if (iframe) iframe.remove();
  }

  render() {
    const formField = this.state.get('manualFormField');
    const isOpenPD = this.state.get('isOpenPD');
    const isOpenPrism = this.state.get('isOpenPrism');
    const rxVisionType = this.state.get('rxVisionType');

    // Check if ADD is required (for Progressive/Multifocal/Bifocal lenses)
    const visionTypeLabel = (rxVisionType?.label || '').toLowerCase();
    const isAddRequired = visionTypeLabel.includes('progressive') ||
                          visionTypeLabel.includes('multifocal') ||
                          visionTypeLabel.includes('bifocal');

    this.innerHTML = `
      <div class="rx-manual-form">
        <!-- Compact table header -->
        <div class="rx-manual-form__table">
          <div class="rx-manual-form__table-header">
            <div class="rx-manual-form__table-col rx-manual-form__table-col--label"></div>
            <div class="rx-manual-form__table-col rx-manual-form__table-col--value">
              <span class="rx-manual-form__eye-label">OD (Right)</span>
            </div>
            <div class="rx-manual-form__table-col rx-manual-form__table-col--value">
              <span class="rx-manual-form__eye-label">OS (Left)</span>
            </div>
          </div>

          <!-- SPH -->
          ${this.renderTableRow('SPH', 'SPH', formField.SPH)}
          <!-- CYL -->
          ${this.renderTableRow('CYL', 'CYL', formField.CYL)}
          <!-- AXIS -->
          ${this.renderTableRow('Axis', 'AXIS', formField.AXIS)}
          <!-- ADD (always visible, required for Progressive) -->
          ${this.renderTableRow('Add', 'ADD', formField.ADD, isAddRequired)}
        </div>

        <!-- PD Section -->
        <div class="rx-manual-form__pd-section">
          <div class="rx-manual-form__pd-header">
            <span class="rx-manual-form__pd-label">Pupil Distance (PD)</span>
            <label class="rx-manual-form__toggle" data-toggle-pd>
              <input type="checkbox" ${isOpenPD ? 'checked' : ''}>
              <span class="rx-manual-form__toggle-text">Two PD values</span>
            </label>
          </div>
          <div class="rx-manual-form__pd-inputs">
            ${!isOpenPD ? `
              <div
                tabindex="0"
                class="rx-form-input rx-form-input--compact rx-form-input--full"
                data-field="PUPIL_RL.RL"
                data-eye="RL"
                data-title="Pupil Distance"
                role="button"
              >
                <span class="rx-form-input__value">${formField.PUPIL_RL.RL}</span>
              </div>
            ` : `
              <div
                tabindex="0"
                class="rx-form-input rx-form-input--compact"
                data-field="PUPIL.R"
                data-eye="R"
                data-title="Pupil Distance Right"
                role="button"
              >
                <span class="rx-form-input__mini-label">R</span>
                <span class="rx-form-input__value">${formField.PUPIL.R}</span>
              </div>
              <div
                tabindex="0"
                class="rx-form-input rx-form-input--compact"
                data-field="PUPIL.L"
                data-eye="L"
                data-title="Pupil Distance Left"
                role="button"
              >
                <span class="rx-form-input__mini-label">L</span>
                <span class="rx-form-input__value">${formField.PUPIL.L}</span>
              </div>
            `}
          </div>
          <button type="button" class="rx-manual-form__measure-pd-btn" data-measure-pd>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
            </svg>
            Measure PD Online
          </button>
        </div>

        <!-- Prism Toggle -->
        <div class="rx-manual-form__prism-toggle">
          <label class="rx-manual-form__toggle" data-toggle-prism>
            <input type="checkbox" ${isOpenPrism ? 'checked' : ''}>
            <span class="rx-manual-form__toggle-text">I have Prism values</span>
          </label>
        </div>

        ${isOpenPrism ? this.renderPrismSection(formField.PRISM) : ''}
      </div>
    `;
  }

  renderTableRow(label, key, values, required = false) {
    const hasValue = (val) => val && val !== 'Select';
    const requiredClass = required ? 'rx-manual-form__table-row--required' : '';
    const requiredIndicator = required ? '<span class="rx-manual-form__required">*</span>' : '';
    const errorClass = (val) => required && !hasValue(val) ? 'rx-form-input--error' : '';

    return `
      <div class="rx-manual-form__table-row ${requiredClass}">
        <div class="rx-manual-form__table-col rx-manual-form__table-col--label">
          <span>${label}${requiredIndicator}</span>
        </div>
        <div class="rx-manual-form__table-col rx-manual-form__table-col--value">
          <div
            tabindex="0"
            class="rx-form-input rx-form-input--compact ${hasValue(values.R) ? 'rx-form-input--filled' : ''} ${errorClass(values.R)}"
            data-field="${key}.R"
            data-eye="R"
            data-title="${label}"
            data-required="${required}"
            role="button"
          >
            <span class="rx-form-input__value">${values.R}</span>
          </div>
        </div>
        <div class="rx-manual-form__table-col rx-manual-form__table-col--value">
          <div
            tabindex="0"
            class="rx-form-input rx-form-input--compact ${hasValue(values.L) ? 'rx-form-input--filled' : ''} ${errorClass(values.L)}"
            data-field="${key}.L"
            data-eye="L"
            data-title="${label}"
            data-required="${required}"
            role="button"
          >
            <span class="rx-form-input__value">${values.L}</span>
          </div>
        </div>
      </div>
    `;
  }

  renderPrismSection(prism) {
    return `
      <div class="rx-manual-form__prism-section">
        <rx-prism-eye eye="R" label="Right Eye (OD)"></rx-prism-eye>
        <rx-prism-eye eye="L" label="Left Eye (OS)"></rx-prism-eye>
      </div>
    `;
  }

  handleSubmit() {
    const formField = this.state.get('manualFormField');
    this.emit('rx:manual-form-submit', { values: formField });
  }
}

// ============================================================================
// RX-PRISM-EYE - Prism Section for One Eye
// ============================================================================

class RxPrismEye extends RxBaseComponent {
  get eye() {
    return this.getAttribute('eye') || 'R';
  }

  get label() {
    return this.getAttribute('label') || '';
  }

  init() {
    this.render();
  }

  setupListeners() {
    this.onStateChange('manualFormField', () => this.render());

    this.listen(this, 'change', (e) => {
      if (e.target.tagName === 'SELECT') {
        const field = e.target.dataset.field;
        if (field) {
          this.state.set(`manualFormField.${field}`, e.target.value);
        }
      }
    });
  }

  render() {
    const formField = this.state.get('manualFormField');
    const prism = formField.PRISM[this.eye];

    this.innerHTML = `
      <div class="rx-prism-eye">
        <span class="rx-prism-eye__label">${this.label}</span>

        <div class="rx-prism-eye__fields">
          <div class="rx-prism-eye__row">
            <div class="rx-prism-eye__field">
              <label class="rx-prism-eye__field-label">Vertical (Δ)</label>
              <div
                tabindex="0"
                class="rx-form-input rx-form-input--compact"
                data-field="PRISM.${this.eye}.vertical.value"
                data-eye="${this.eye}"
                data-title="Prism Vertical"
                role="button"
              >
                <span class="rx-form-input__value">${prism.vertical.value}</span>
              </div>
            </div>
            <div class="rx-prism-eye__field">
              <label class="rx-prism-eye__field-label">Direction</label>
              <select
                class="rx-prism-eye__select"
                data-field="PRISM.${this.eye}.vertical.direction"
              >
                <option value="" ${!prism.vertical.direction ? 'selected' : ''}>Select</option>
                <option value="U" ${prism.vertical.direction === 'U' ? 'selected' : ''}>Up</option>
                <option value="D" ${prism.vertical.direction === 'D' ? 'selected' : ''}>Down</option>
              </select>
            </div>
          </div>

          <div class="rx-prism-eye__row">
            <div class="rx-prism-eye__field">
              <label class="rx-prism-eye__field-label">Horizontal (Δ)</label>
              <div
                tabindex="0"
                class="rx-form-input rx-form-input--compact"
                data-field="PRISM.${this.eye}.horizontal.value"
                data-eye="${this.eye}"
                data-title="Prism Horizontal"
                role="button"
              >
                <span class="rx-form-input__value">${prism.horizontal.value}</span>
              </div>
            </div>
            <div class="rx-prism-eye__field">
              <label class="rx-prism-eye__field-label">Base Direction</label>
              <select
                class="rx-prism-eye__select"
                data-field="PRISM.${this.eye}.horizontal.direction"
              >
                <option value="" ${!prism.horizontal.direction ? 'selected' : ''}>Select</option>
                <option value="I" ${prism.horizontal.direction === 'I' ? 'selected' : ''}>In</option>
                <option value="O" ${prism.horizontal.direction === 'O' ? 'selected' : ''}>Out</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

// ============================================================================
// RX-VALUE-DRAWER - Drawer with Value Selection
// ============================================================================

class RxValueDrawer extends RxBaseComponent {
  #isOpen = false;
  #fieldKey = '';
  #title = '';

  init() {
    this.render();
  }

  setupListeners() {
    this.listen(document, 'rx:open-value-drawer', (e) => {
      this.#isOpen = true;
      this.#fieldKey = e.detail.fieldKey;
      this.#title = e.detail.title;
      this.render();
    });

    // Use document listener since component has display:contents
    this.listen(document, 'click', (e) => {
      if (!this.#isOpen) return;

      // Close on backdrop click
      if (e.target.classList.contains('rx-value-drawer__backdrop')) {
        this.close();
        return;
      }

      // Close on close button click
      if (e.target.closest('.rx-value-drawer__close')) {
        this.close();
        return;
      }

      // Select value
      const valueBtn = e.target.closest('.rx-value-drawer__option');
      if (valueBtn) {
        this.updateFormField(this.#fieldKey, valueBtn.dataset.value);
        this.close();
      }
    });
  }

  updateFormField(fieldKey, value) {
    const formField = this.state.get('manualFormField');
    const keys = fieldKey.split('.');

    // Navigate to nested property and set value
    let target = formField;
    for (let i = 0; i < keys.length - 1; i++) {
      target = target[keys[i]];
    }
    target[keys[keys.length - 1]] = value;

    // Trigger re-render by setting the whole object
    this.state.set('manualFormField', { ...formField });
  }

  close() {
    this.#isOpen = false;
    this.render();
  }

  render() {
    if (!this.#isOpen) {
      this.innerHTML = '';
      return;
    }

    const values = this.getValuesForField(this.#fieldKey);
    const columnsHtml = this.renderColumns(values);

    this.innerHTML = `
      <div class="rx-value-drawer__backdrop" data-close></div>
      <div class="rx-value-drawer__content">
        <div class="rx-value-drawer__handle"></div>
        <div class="rx-value-drawer__header">
          <span class="rx-value-drawer__title">${this.#title}</span>
          <button type="button" class="rx-value-drawer__close" data-close aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>
        <div class="rx-value-drawer__body">
          ${columnsHtml}
        </div>
      </div>
    `;
  }

  renderColumns(values) {
    // SPH/CYL with zero, positive, negative
    if (values.zero !== undefined) {
      return `
        <button type="button" class="rx-value-drawer__option rx-value-drawer__option--wide" data-value="${values.zero}">${values.zero}</button>
        <div class="rx-value-drawer__columns">
          <div class="rx-value-drawer__column">
            ${values.positive.map(v => `
              <button type="button" class="rx-value-drawer__option" data-value="${v}">${v}</button>
            `).join('')}
          </div>
          <div class="rx-value-drawer__column">
            ${values.negative.map(v => `
              <button type="button" class="rx-value-drawer__option" data-value="${v}">${v}</button>
            `).join('')}
          </div>
        </div>
      `;
    }

    // Regular array - alternate columns
    const leftColumn = [];
    const rightColumn = [];
    values.forEach((v, i) => {
      if (i % 2 === 0) {
        leftColumn.push(v);
      } else {
        rightColumn.push(v);
      }
    });

    return `
      <div class="rx-value-drawer__columns">
        <div class="rx-value-drawer__column">
          ${leftColumn.map(v => `
            <button type="button" class="rx-value-drawer__option" data-value="${v}">${v}</button>
          `).join('')}
        </div>
        <div class="rx-value-drawer__column">
          ${rightColumn.map(v => `
            <button type="button" class="rx-value-drawer__option" data-value="${v}">${v}</button>
          `).join('')}
        </div>
      </div>
    `;
  }

  getValuesForField(fieldKey) {
    const limits = window.rxLimits || window.themeVariables?.settings?.rxLimits || {};

    // SPH/CYL values
    if (fieldKey.includes('SPH') || fieldKey.includes('CYL')) {
      const max = fieldKey.includes('SPH')
        ? (limits.sphMax || 8)
        : (limits.cylMax || 8);
      const positive = [];
      const negative = [];
      for (let i = 0.25; i <= max; i += 0.25) {
        positive.push(`+${i.toFixed(2)}`);
        negative.push(`-${i.toFixed(2)}`);
      }
      return { zero: '0.00', positive, negative };
    }

    // Axis values
    if (fieldKey.includes('AXIS')) {
      const min = limits.axisMin || 1;
      const max = limits.axisMax || 180;
      const values = [];
      for (let i = min; i <= max; i++) {
        values.push(i.toString());
      }
      return values;
    }

    // PD values - Two PD (PUPIL.R / PUPIL.L) has different limits than single PD (PUPIL_RL)
    if (fieldKey.includes('PUPIL')) {
      const isTwoPd = fieldKey === 'PUPIL.R' || fieldKey === 'PUPIL.L';
      const min = isTwoPd ? (limits.twoPdMin || 20) : (limits.pdMin || 45);
      const max = isTwoPd ? (limits.twoPdMax || 45) : (limits.pdMax || 90);
      const values = [];
      for (let i = min; i <= max; i += 0.5) {
        values.push(i.toFixed(1));
      }
      return values;
    }

    // ADD values
    if (fieldKey.includes('ADD')) {
      const max = limits.addMax || 4;
      const values = [];
      for (let i = 0.25; i <= max; i += 0.25) {
        values.push(`+${i.toFixed(2)}`);
      }
      return values;
    }

    // Prism values
    if (fieldKey.includes('PRISM')) {
      const max = limits.prismMax || 10;
      const values = [];
      for (let i = 0; i <= max; i += 0.25) {
        values.push(i.toFixed(2));
      }
      return values;
    }

    return [];
  }
}

// ============================================================================
// RX-STICKY-ATC - Sticky Add to Cart Bar
// ============================================================================

class RxStickyAtc extends RxBaseComponent {
  init() {
    this.render();
  }

  setupListeners() {
    this.onStateChange('product', () => this.render());
    this.onStateChange('selectedVariant', () => this.render());
    this.onStateChange('rxLensSelectedVariant', () => this.render());
    this.onStateChange('isOpenPrescriptionPopup', () => this.updateVisibility());
    this.onStateChange('isOpenFramePopup', () => this.updateVisibility());

    this.listen(this, 'click', (e) => {
      if (e.target.closest('[data-add-to-cart]')) {
        this.emit('rx:prescription-open');
      }
    });
  }

  updateVisibility() {
    const isOpenPrescription = this.state.get('isOpenPrescriptionPopup');
    const isOpenFrame = this.state.get('isOpenFramePopup');
    this.hidden = isOpenPrescription || isOpenFrame;
  }

  render() {
    const product = this.state.get('product');
    const selectedVariant = this.state.get('selectedVariant');
    const rxLensSelectedVariant = this.state.get('rxLensSelectedVariant');

    if (!product || !selectedVariant) return;

    const frameName = this.getFrameName(product);
    const frameColor = this.getFrameColor(product);
    const totalPrice = (selectedVariant.price || 0) + (rxLensSelectedVariant?.price || 0);
    const compareAtPrice = this.getCompareAtPrice(selectedVariant, rxLensSelectedVariant);

    this.innerHTML = `
      <div class="rx-sticky-atc__bar">
        <div class="rx-sticky-atc__info">
          <div class="rx-sticky-atc__product">
            <h2 class="rx-sticky-atc__title">${frameName}</h2>
            <span class="rx-sticky-atc__subtitle">${frameColor}</span>
          </div>
          <div class="rx-sticky-atc__pricing">
            <span class="rx-sticky-atc__price">${RxUtils.formatMoney(totalPrice)}</span>
            ${compareAtPrice ? `<s class="rx-sticky-atc__compare-price">${RxUtils.formatMoney(compareAtPrice)}</s>` : ''}
          </div>
        </div>
        <button
          type="button"
          class="rx-sticky-atc__button"
          data-add-to-cart
        >
          Add to cart
        </button>
      </div>
    `;
  }

  getFrameName(product) {
    const frameTag = product?.tags?.find(t => t.includes('frame_'));
    return frameTag ? frameTag.replace('frame_', '').split('~')[0] : '';
  }

  getFrameColor(product) {
    const frameTag = product?.tags?.find(t => t.includes('frame_'));
    return frameTag?.split('~')[1] || '';
  }

  getCompareAtPrice(selectedVariant, rxLensSelectedVariant) {
    let originalPrice = 0;
    let newPrice = 0;

    if (selectedVariant?.compare_at_price > selectedVariant?.price) {
      originalPrice += selectedVariant.compare_at_price;
      newPrice += selectedVariant.price;
    } else {
      originalPrice += selectedVariant?.price || 0;
      newPrice += selectedVariant?.price || 0;
    }

    if (rxLensSelectedVariant?.compare_at_price > rxLensSelectedVariant?.price) {
      originalPrice += rxLensSelectedVariant.compare_at_price;
      newPrice += rxLensSelectedVariant.price;
    } else {
      originalPrice += rxLensSelectedVariant?.price || 0;
      newPrice += rxLensSelectedVariant?.price || 0;
    }

    return originalPrice > newPrice ? originalPrice : null;
  }
}

// ============================================================================
// RX-PRICE-DISPLAY - Price Display Component
// ============================================================================

class RxPriceDisplay extends RxBaseComponent {
  init() {
    this.render();
  }

  setupListeners() {
    this.onStateChange('selectedVariant', () => this.render());
    this.onStateChange('rxLensSelectedVariant', () => this.render());
  }

  render() {
    const selectedVariant = this.state.get('selectedVariant');
    const rxLensSelectedVariant = this.state.get('rxLensSelectedVariant');

    const totalPrice = (selectedVariant?.price || 0) + (rxLensSelectedVariant?.price || 0);

    this.innerHTML = `
      <span class="rx-price-display__price">${RxUtils.formatMoney(totalPrice)}</span>
    `;
  }
}

// ============================================================================
// RX-VARIANT-SKU - Frame SKU, synced with the selected frame variant
// ============================================================================

class RxVariantSku extends RxBaseComponent {
  init() {
    this.render();
  }

  setupListeners() {
    this.onStateChange('selectedVariant', () => this.render());
  }

  render() {
    const selectedVariant = this.state.get('selectedVariant');
    // Keep the server-rendered SKU until state is populated
    if (!selectedVariant) return;

    this.textContent = `SKU: ${selectedVariant.sku || ''}`;
  }
}

// ============================================================================
// RX-FRAME-TRIGGER - Frame Popup Trigger Button
// ============================================================================

class RxFrameTrigger extends RxBaseComponent {
  init() {
    this.render();
  }

  setupListeners() {
    this.onStateChange('product', () => this.render());
    this.onStateChange('isOpenFramePopup', () => this.render());

    this.listen(this, 'click', () => {
      this.emit('rx:frame-popup-toggle');
    });
  }

  render() {
    const product = this.state.get('product');
    const isOpen = this.state.get('isOpenFramePopup');
    const frameName = this.getFrameName(product);

    this.toggleAttribute('open', isOpen);

    this.innerHTML = `
      <button
        type="button"
        class="rx-frame-trigger__button"
        aria-label="Frame popup button"
        aria-expanded="${isOpen}"
      >
        <span>${frameName}</span>
        <span class="rx-frame-trigger__chevron">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M2.26414 11.2323C2.43332 11.4037 2.66274 11.5 2.90196 11.5C3.14118 11.5 3.37061 11.4037 3.53978 11.2323L8.00545 6.7068L12.4711 11.2323C12.6413 11.3989 12.8691 11.491 13.1057 11.4889C13.3422 11.4868 13.5685 11.3907 13.7358 11.2212C13.903 11.0517 13.9979 10.8224 14 10.5827C14.002 10.3429 13.9111 10.112 13.7468 9.93958L8.64327 4.76768C8.47409 4.59628 8.24467 4.5 8.00545 4.5C7.76623 4.5 7.5368 4.59628 7.36762 4.76768L2.26414 9.93958C2.09501 10.111 2 10.3435 2 10.5859C2 10.8284 2.09501 11.0609 2.26414 11.2323Z"/></svg>
        </span>
      </button>
    `;
  }

  getFrameName(product) {
    const frameTag = product?.tags?.find(t => t.includes('frame_'));
    return frameTag ? frameTag.replace('frame_', '').split('~')[0] : '';
  }
}

// ============================================================================
// REGISTER ALL COMPONENTS
// ============================================================================

const components = {
  'rx-product-page': RxProductPage,
  'rx-gallery': RxGallery,
  'rx-lens-selector': RxLensSelector,
  'rx-vision-selector': RxVisionSelector,
  'rx-lens-options': RxLensOptions,
  'rx-color-picker': RxColorPicker,
  'rx-frame-selector': RxFrameSelector,
  'rx-frame-popup': RxFramePopup,
  'rx-frame-trigger': RxFrameTrigger,
  'rx-prescription-drawer': RxPrescriptionDrawer,
  'rx-manual-form': RxManualForm,
  'rx-prism-eye': RxPrismEye,
  'rx-value-drawer': RxValueDrawer,
  'rx-sticky-atc': RxStickyAtc,
  'rx-price-display': RxPriceDisplay,
  'rx-variant-sku': RxVariantSku
  // Note: RxFileUpload removed - functionality merged into RxPrescriptionDrawer
};

Object.entries(components).forEach(([name, component]) => {
  if (!window.customElements.get(name)) {
    window.customElements.define(name, component);
  }
});

// Make components available globally for debugging
window.RxComponents = {
  RxState,
  RxUtils,
  RxBaseComponent,
  RxProductPage,
  RxGallery,
  RxLensSelector,
  RxVisionSelector,
  RxLensOptions,
  RxColorPicker,
  RxFrameSelector,
  RxFramePopup,
  RxPrescriptionDrawer,
  RxManualForm,
  RxValueDrawer,
  RxStickyAtc,
  RxPriceDisplay
};
